import { useTranslation } from 'react-i18next';
import { ScanLine } from 'lucide-react';

/**
 * Unified loading state for the Scan Station route. Used by:
 *   - the route's Suspense fallback (lazy chunk load)
 *   - StadiumOperatorRoute while the access probe is in flight
 *   - ScanStation shell while event + sections data are loading
 *
 * IMPORTANT: styles are INLINE. This component is rendered by the Suspense
 * fallback before the lazy ScanStation chunk (which owns scan-station.css)
 * has loaded — so any external CSS dependency would mean an unstyled flash
 * of raw text top-left while the chunk downloads. Inline styles avoid that.
 */
export function ScanStationLoading() {
  const { t } = useTranslation();
  return (
    <div style={overlayStyle}>
      <div style={innerStyle}>
        <ScanLine size={36} style={iconStyle} aria-hidden />
        <p style={titleStyle}>{t('scanStation.title')}</p>
        <p style={subStyle}>{t('common.loading')}</p>
      </div>
      <style>{keyframes}</style>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: '#0b0b0c',
  color: '#f8fafc',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const innerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.5rem',
};

const iconStyle: React.CSSProperties = {
  color: '#fbbf24',
  animation: 'scan-station-loading-pulse 1.6s ease-in-out infinite',
};

const titleStyle: React.CSSProperties = {
  fontFamily: "'Crimson Pro', serif",
  fontSize: '1.4rem',
  fontWeight: 600,
  margin: 0,
};

const subStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  opacity: 0.55,
  margin: 0,
};

const keyframes = `
@keyframes scan-station-loading-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.5; transform: scale(0.95); }
}
`;

export default ScanStationLoading;
